/**
 * Current arriving through something that was dark.
 *
 * A surge is not random flicker. It has a shape - a dead lead, a weak stutter,
 * a brighter one, then it holds - and only the timings inside that shape are
 * drawn fresh each time. Randomising the shape as well produces static, which
 * reads as a broken element rather than as one coming to life.
 *
 * The pattern is generated rather than written as CSS keyframes because a
 * keyframe block is fixed at build time: the board already carries three
 * hand-written flicker patterns for exactly this reason, and three is not
 * enough for a cue a player sees on every turn.
 */

/**
 * Every surge in the app, and the only place to tune one.
 *
 * Each entry is bounded jitter around the shape: [min, max] in milliseconds,
 * except bursts, which is a count. Nothing else in the codebase should hold
 * flicker timings - if something new needs to strike, it gets a preset here.
 */
export const SURGES = {
    /**
     * A guess built while the turn was elsewhere, taking current when it
     * lands. Slower and heavier than a row: it is permission arriving, and the
     * player has been looking at the dark row while they waited.
     */
    guess: {
        leadMs: [40, 130],
        bursts: [2, 4],
        flashMs: [35, 95],
        gapMs: [45, 140],
        holdMs: [180, 300],
    },

    /**
     * A row landing on the board, its wire striking like a tube coming on.
     * Quicker off the mark, with longer dead gaps, so several rows arriving
     * together still read as separate events.
     */
    row: {
        leadMs: [30, 90],
        bursts: [2, 3],
        flashMs: [40, 90],
        gapMs: [70, 220],
        holdMs: [150, 320],
    },

    /**
     * The board when the code goes. Every row strikes, one after another, so
     * the win runs back through the whole game rather than stopping at the row
     * that won it.
     *
     * Eager where the others hesitate - a short lead, more bursts, tight gaps -
     * and it holds far longer at the end, because nothing follows it. stepMs is
     * the wait before the next row takes current; only this preset has one,
     * since only this one travels.
     */
    win: {
        leadMs: [10, 50],
        bursts: [3, 5],
        flashMs: [30, 70],
        gapMs: [35, 90],
        holdMs: [420, 700],
        stepMs: [60, 120],
    },
};


/** A value inside a [min, max] range. */
export function between([min, max], rand = Math.random) {
    return min + rand() * (max - min);
}

/**
 * Builds one surge.
 *
 * Duration is derived from the parts rather than drawn separately, so the total
 * can never contradict the flashes it is supposed to contain.
 *
 * Each flash is brighter than the last, which is what makes the sequence read
 * as building towards the hold rather than as switching on and off.
 */
export function surgePattern(params, rand = Math.random) {
    const bursts = Math.round(between(params.bursts, rand));
    const steps = [{ atMs: 0, lit: false, intensity: 0 }];

    let at = between(params.leadMs, rand);

    for (let i = 0; i < bursts; i++) {
        steps.push({ atMs: at, lit: true, intensity: (i + 1) / (bursts + 1) });
        at += between(params.flashMs, rand);
        steps.push({ atMs: at, lit: false, intensity: 0 });
        at += between(params.gapMs, rand);
    }

    steps.push({ atMs: at, lit: true, intensity: 1 });

    return { steps, durationMs: at + between(params.holdMs, rand) };
}

/**
 * Turns a pattern into Web Animations keyframes.
 *
 * `render` maps one step to CSS, so a single pattern can drive things that look
 * nothing alike. steps(1, end) holds each state flat and snaps between them -
 * current does not fade up.
 */
export function keyframesFor({ steps, durationMs }, render) {
    return steps.map((step) => ({
        ...render(step),
        offset: Math.min(1, step.atMs / durationMs),
        easing: 'steps(1, end)',
    }));
}

/**
 * The one way anything in the app strikes: name a preset, give it targets.
 *
 * Draws a fresh pattern and runs it, returning the pattern so a caller that
 * needs to know how long it lasts can ask rather than assume.
 */
export function surge(name, targets, { scope = globalThis, rand = Math.random, delayMs = 0 } = {}) {
    const params = SURGES[name];
    if (!params) throw new Error(`surge: no preset named ${name}`);

    const pattern = surgePattern(params, rand);
    runSurge(targets, pattern, scope, delayMs);
    return pattern;
}

/**
 * The same surge travelling along several targets, striking them in turn.
 *
 * Each target draws its own pattern, so a row still reads as a row coming to
 * life rather than as every wire thrown by one switch. What ties them together
 * is the order: each waits a beat longer than the one before, jittered, so the
 * wave arrives like current finding its way rather than like a metronome.
 *
 * A target that has not struck yet is held dark for its wait - that is what
 * makes the travel visible, and why a board that is already lit goes out ahead
 * of the wave rather than brightening in place.
 */
export function cascade(name, targets, { scope = globalThis, rand = Math.random } = {}) {
    const step = SURGES[name] && SURGES[name].stepMs;
    if (!step) throw new Error(`cascade: ${name} does not travel`);

    let delayMs = 0;
    return targets.map((target) => {
        const pattern = surge(name, [target], { scope, rand, delayMs });
        delayMs += between(step, rand);
        return pattern;
    });
}

/** A wire, anywhere on the board. Its lit colour comes from the row it is in. */
export function wireTarget(el) {
    return { el, render: wireFrame, pseudoElement: '::before' };
}

/** The guess button's ring. */
export function buttonTarget(el) {
    return { el, render: buttonFrame, pseudoElement: '::before' };
}

/** The wire: its lit colour comes from the row it is in, so this never names one. */
export function wireFrame({ lit, intensity }) {
    if (!lit) return { background: '#1b202b', boxShadow: 'none' };
    return {
        background: 'var(--wire-lit, #c2ccdd)',
        boxShadow: `0 0 ${(6 + intensity * 12).toFixed(1)}px var(--wire-glow, rgba(255, 255, 255, ${(0.2 + intensity * 0.35).toFixed(2)}))`,
    };
}

/**
 * The guess button's ring, dark to white.
 *
 * Only the ring, never the face behind it: the cap is a black object with a
 * cut in it, and a face that brightened would make the whole button a lamp.
 * The same two properties the wire strikes on, for the same reason - they are
 * one circuit closing, and the ring's glow has to build as the wire's does.
 */
export function buttonFrame({ lit, intensity }) {
    if (!lit) return { borderColor: '#1f2532', boxShadow: 'none' };
    return {
        borderColor: '#ffffff',
        boxShadow: `0 0 ${(6 + intensity * 6).toFixed(1)}px -1px rgba(255, 255, 255, ${(0.2 + intensity * 0.3).toFixed(2)})`,
    };
}

/**
 * Runs one pattern across several targets at once.
 *
 * They share the pattern rather than each drawing their own, because the wire
 * and the button are one circuit closing: two independent rolls would visibly
 * disagree about when the current arrived.
 *
 * Both targets are pseudo-elements, which not every engine will animate. The
 * fallback is no flicker at all - the element simply is lit, which is where the
 * animation was going to leave it anyway.
 *
 * fill is backwards, not both: a delayed surge still sits dark until its moment,
 * but once it has struck the element goes back to being coloured by its class.
 * A filled animation would instead pin the last frame at the animation origin,
 * which outranks every rule in the stylesheet - the ring would stay lit through
 * being sent, refused and disabled, because none of those could paint over it.
 */
export function runSurge(targets, pattern, scope = globalThis, delayMs = 0) {
    if (reducedMotion(scope)) return [];

    const running = [];
    for (const { el, render, pseudoElement } of targets) {
        if (!el || typeof el.animate !== 'function') continue;
        try {
            running.push(el.animate(keyframesFor(pattern, render), {
                duration: pattern.durationMs,
                delay: delayMs,
                fill: 'backwards',
                pseudoElement,
            }));
        } catch {
            // No pseudo-element animation here; it lands lit regardless.
        }
    }
    return running;
}

/** Whether the player has asked for motion to be kept still. */
export function reducedMotion(scope = globalThis) {
    if (!scope || typeof scope.matchMedia !== 'function') return false;
    return scope.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
